import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import {
  Building2, ShoppingCart, TrendingUp, FileText,
  Plus, Users, RefreshCw, Calendar, Clock, CheckCircle, Package
} from 'lucide-react';

const PRIMARY = '#0099FF';
const PRIMARY_LIGHT = 'rgba(0, 153, 255, 0.08)';

const ComercialHome = () => {
  const { user, token, API_URL } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/dashboard/commercial-stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setStats(await res.json());
    } catch (err) {
      console.error('Error fetching commercial stats:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [API_URL, token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: PRIMARY }} />
      </div>
    );
  }

  const kpis = [
    { label: 'Mis Clientes', value: stats?.totalClientes || 0, icon: Building2, bg: 'bg-blue-50', border: 'border-blue-100', text: 'text-blue-700' },
    { label: 'Pedidos Pendientes', value: stats?.pedidosPendientes || 0, icon: ShoppingCart, bg: 'bg-amber-50', border: 'border-amber-100', text: 'text-amber-700' },
    { label: 'Pedidos este Mes', value: stats?.pedidosMes || 0, icon: TrendingUp, bg: 'bg-green-50', border: 'border-green-100', text: 'text-green-700' },
    { label: 'Ofertas Pendientes', value: stats?.ofertasPendientes || 0, icon: FileText, bg: 'bg-purple-50', border: 'border-purple-100', text: 'text-purple-700' },
  ];

  return (
    <div className="h-full flex flex-col">

      {/* Top bar: Welcome + KPIs + Quick Actions */}
      <div className="bg-white border-b border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <img src="/logo.png" alt="ComprasSanitarias" className="h-16 w-auto" />
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                Bienvenido, {user?.nombrec || user?.username}
              </h1>
              <p className="text-sm text-gray-500">
                Portal Comercial &mdash; {new Date().toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
              </p>
            </div>
          </div>
          <button
            onClick={fetchStats}
            className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200 flex items-center gap-2"
          >
            <RefreshCw size={14} /> Actualizar
          </button>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {kpis.map((kpi) => (
            <div key={kpi.label} className={`${kpi.bg} rounded-lg p-5 border ${kpi.border}`}>
              <div className="flex items-center justify-between mb-3">
                <kpi.icon size={22} className={kpi.text} />
              </div>
              <p className={`text-3xl font-bold ${kpi.text}`}>{kpi.value}</p>
              <p className={`text-sm mt-1 ${kpi.text} opacity-80`}>{kpi.label}</p>
            </div>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="flex flex-wrap gap-3">
          <Link
            to="/pedidos/nuevo"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-white font-medium text-sm transition-colors duration-200 hover:opacity-90"
            style={{ backgroundColor: PRIMARY }}
          >
            <Plus size={16} />
            Nuevo Pedido
          </Link>
          <Link
            to="/comercial/clientes"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium text-sm border-2 transition-colors duration-200 hover:bg-gray-50"
            style={{ borderColor: PRIMARY, color: PRIMARY }}
          >
            <Users size={16} />
            Ver Mis Clientes
          </Link>
        </div>
      </div>

      {/* Orders header bar */}
      <div className="bg-white shadow-sm border-b border-gray-200 p-6">
        <div className="flex items-center space-x-4">
          <div className="p-3 rounded-full" style={{ backgroundColor: 'rgba(0, 153, 255, 0.08)' }}>
            <ShoppingCart className="w-6 h-6" style={{ color: PRIMARY }} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Últimos Pedidos</h2>
            <p className="text-sm text-gray-500">Pedidos de tus clientes</p>
          </div>
        </div>
      </div>

      {/* Orders table */}
      <div className="flex-1 overflow-auto p-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pedido</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cliente</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {stats?.pedidosRecientes?.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Package className="w-4 h-4 text-gray-400 mr-2" />
                        <span className="text-sm font-medium text-gray-900">#{p.npedido || p.id}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{p.cliente_nombre || '-'}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-500">
                        <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                        {p.fecha ? new Date(p.fecha).toLocaleDateString('es-ES') : '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        p.estado === 1 ? 'bg-amber-100 text-amber-800' :
                        p.estado === 2 ? 'bg-green-100 text-green-800' :
                        p.estado === 3 ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {p.estado === 1 ? <Clock size={14} /> : p.estado === 2 ? <CheckCircle size={14} /> : <Package size={14} />}
                        {p.estado === 1 ? 'Abierto' : p.estado === 2 ? 'Confirmado' : p.estado === 3 ? 'Enviado' : 'Envío parcial'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-gray-900">
                      {p.itotaliva != null ? `${Number(p.itotaliva).toFixed(2)} €` : '-'}
                    </td>
                  </tr>
                ))}
                {(!stats?.pedidosRecientes || stats.pedidosRecientes.length === 0) && (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center">
                      <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-sm text-gray-500">No hay pedidos</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComercialHome;
